/**
 * Utility functions for filtering transaction data
 */

/**
 * Normalize transaction data
 */
export const normalizeData = (data) => {
  if (!data) return data;
  return data.map(row => ({
    ...row,
    tags: (row.tags || []).map(tag => tag.toLowerCase().trim())
  }));
};

/**
 * Process and prepare transaction data for display
 */
export const processTransactionData = (data) => {
  if (!data || data.length === 0) return { processedData: [], minAmount: 0, maxAmount: 0, minDate: new Date(), maxDate: new Date(), allTags: new Set() };

  // Normalize the data
  data = normalizeData(data);

  // Process the data for display
  const processedData = data.map((row, index) => ({
    ...row,
    id: index, // Add unique ID for DataGrid
    tags: (row.tags || []).sort(), // Sort tags when loading data
    tagInput: '',
    tax: row.tax || false,
    comments: row.comments || ''
  }));

  // Calculate min and max amounts
  const amounts = processedData.map(row => parseFloat(row.amount));
  const minAmount = Math.floor(Math.min(...amounts));
  const maxAmount = Math.ceil(Math.max(...amounts));

  // Calculate min and max dates
  const dates = processedData.map(row => new Date(row.date));
  const minDate = new Date(Math.min(...dates)) ?? (new Date()).setFullYear((new Date()).getFullYear() - 1);
  const maxDate = new Date(Math.max(...dates)) ?? new Date();

  // Collect all existing tags
  const tags = new Set();
  processedData.forEach(row => {
    row.tags.forEach(tag => tags.add(tag));
  });

  return { 
    processedData, 
    minAmount, 
    maxAmount, 
    minDate, 
    maxDate, 
    allTags: tags 
  };
};

/**
 * Filter transaction data based on filter criteria
 */
export const filterTransactionData = (tableData, filters) => {
  // Early return if no data
  if (!tableData.length) return [];

  // Pre-compute filter values to avoid repeated computation
  const searchText = filters.searchText.toLowerCase();
  const description = filters.description.toLowerCase();
  const description2 = filters.description2.toLowerCase();
  const comments = filters.comments.toLowerCase();
  const filterStartDate = filters.filterStartDate ? filters.filterStartDate.getTime() : null;
  const filterEndDate = filters.filterEndDate ? filters.filterEndDate.getTime() : null;

  return tableData.map((row, originalIndex) => ({ ...row, originalIndex }))
    .filter(row => {
      // Early return for untagged filter
      if (filters.showUntagged && row.tags.length > 0) return false;

      // Early return for tag filter
      if (!filters.showUntagged && filters.tags.length > 0) {
        if (!filters.tags.every(tag => row.tags.includes(tag))) return false;
      }

      // Generic search text - early return
      if (searchText) {
        const rowText = `${row.date} ${row.amount} ${row.description} ${row.description2} ${row.comments} ${row.tags.join(' ')}`.toLowerCase();
        if (!rowText.includes(searchText)) return false;
      }

      // Description filters - early return
      if (description && !row.description.toLowerCase().includes(description)) return false;
      if (description2 && !row.description2.toLowerCase().includes(description2)) return false;

      // Comments filter - early return
      if (comments && !row.comments.toLowerCase().includes(comments)) return false;

      // Date range filter - early return
      if (filterStartDate || filterEndDate) {
        const rowDate = new Date(row.date).getTime();
        if (filterStartDate && rowDate < filterStartDate) return false;
        if (filterEndDate && rowDate > filterEndDate) return false;
      }

      // Amount range filter - early return
      const amount = parseFloat(row.amount);
      if (amount < filters.filterMinAmount || amount > filters.filterMaxAmount) return false;

      // Tax filter - early return
      if (!filters.tax[row.tax]) return false;

      return true;
    });
};